import { useEffect, useState } from 'react';
import { Plus, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { superadminService } from '@/services/superadminService';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AdminPageShell, PageHeader, PageLoading } from '@/components/admin/shell';
import { AdminButton } from '@/components/admin/shell/AdminButton';

interface Restaurant {
  id: string;
  name: string;
  created_at: string;
}

interface RestaurantFormData {
  name: string;
}

const emptyFormData: RestaurantFormData = {
  name: '',
};

export function RestaurantManagementPage() {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState<Restaurant | null>(null);
  const [formData, setFormData] = useState<RestaurantFormData>(emptyFormData);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadRestaurants();
  }, []);

  const loadRestaurants = async () => {
    setIsLoading(true);
    try {
      const response = await superadminService.getRestaurants();
      setRestaurants(response.restaurants as Restaurant[]);
    } catch {
      toast.error('Error al cargar los restaurantes');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingRestaurant(null);
    setFormData(emptyFormData);
    setShowDialog(true);
  };

  const openEditDialog = (restaurant: Restaurant) => {
    setEditingRestaurant(restaurant);
    setFormData({
      name: restaurant.name,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setIsSaving(true);
    try {
      if (editingRestaurant) {
        await superadminService.updateRestaurant(editingRestaurant.id, {
          restaurant: {
            name: formData.name,
          },
        });
        toast.success('Restaurante actualizado correctamente');
      } else {
        await superadminService.createRestaurant({
          restaurant: {
            name: formData.name,
            provider: 'square',
          },
        });
        toast.success('Restaurante creado correctamente');
      }
      setShowDialog(false);
      loadRestaurants();
    } catch (error: unknown) {
      const apiError = error as { response?: { data?: { error?: string; details?: string[] } } };
      const message = apiError?.response?.data?.error ||
        (apiError?.response?.data?.details as string[])?.join(', ') ||
        'Error al guardar el restaurante';
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <AdminPageShell>
        <div className="space-y-6">
          <PageHeader title="Gestión de Restaurantes" />
          <PageLoading variant="list" rows={5} />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <PageHeader title="Gestión de Restaurantes">
          <AdminButton variant="primary" onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Restaurante
          </AdminButton>
        </PageHeader>

      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Nombre</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Creado</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {restaurants.map((restaurant) => (
              <tr key={restaurant.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium">{restaurant.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600">
                  {new Date(restaurant.created_at).toLocaleDateString('es-ES')}
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(restaurant)}
                    className="h-8 w-8"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {editingRestaurant ? 'Editar Restaurante' : 'Nuevo Restaurante'}
            </DialogTitle>
            <DialogDescription>
              {editingRestaurant
                ? 'Modifica los datos del restaurante.'
                : 'Crea un nuevo restaurante en el sistema.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Casa Marcelo"
                className="min-h-11"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="min-h-11">
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={isSaving} className="min-h-11">
              {isSaving ? 'Guardando...' : editingRestaurant ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminPageShell>
  );
}
